import classnames from 'classnames'
import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Skeleton } from '@src/quince-components/src'
import Image from '@src/quince-components/src/atoms/image'
import Link from '@src/quince-components/src/atoms/link'
import { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import GridSlider from '@src/SectionRenderer/sections/gridSlider'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { ALL_SUBCOLLECTION_FILTER_ID } from '../../collection.constants'
import { SubCollection } from '../../domains/Collection'
import ContentfulCollectionMedia from '../../types/ContentfulCollectionMedia'

import styles from './subCollectionCardList.module.scss'

const ALL_SUBCOLLECTION_TITLE = 'All'
const SUB_COLLECTION_CARD_TITLE_DATA_TYPE = 'sub-collection-card-title'

interface SubCollectionCardListProps {
  subCollections: SubCollection[]
  className?: string
  onCardClick: (subCollection: SubCollection) => void
  slug: string
  isSubCollectionSelected?: (subCollection: SubCollection) => boolean
  collectionExploreEverythingImage?: ContentfulCollectionMedia
  contentful_id: string
  isNewCLPFiltersExperimentActive: boolean
  showMobileFilterWithDrawer: boolean
  isNewCLPFiltersDesktopActive: boolean
  showLoader?: boolean
}

const SubCollectionCardList = ({
  subCollections = [],
  className,
  onCardClick,
  isSubCollectionSelected,
  collectionExploreEverythingImage,
  contentful_id,
  isNewCLPFiltersExperimentActive,
  showMobileFilterWithDrawer,
  isNewCLPFiltersDesktopActive,
  showLoader,
}: SubCollectionCardListProps) => {
  const path = usePathname()
  const deviceInfo = useDeviceInfo()

  const [headerHeight, setHeaderHeight] = useState(0)

  const [intersectRef, isNotScrolledDown] = useIntersect(null, '0px', 0, true) as unknown as [
    React.RefObject<HTMLDivElement>,
    boolean,
  ]

  const isScrolledDown = !isNotScrolledDown
  const globalIsScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = !isDesktop

  // Create "All" subcollection if experiment is active and image is provided
  const allSubCollections = useMemo(() => {
    if (isNewCLPFiltersExperimentActive && collectionExploreEverythingImage && subCollections.length > 1) {
      // Convert the contentful image to the format expected by SubCollection
      const imageDesktop = new ImageDomain({
        id: collectionExploreEverythingImage.id,
        title: collectionExploreEverythingImage.title,
        description: collectionExploreEverythingImage.description,
        url: collectionExploreEverythingImage.file.url,
        fileName: collectionExploreEverythingImage.file.fileName,
        height: collectionExploreEverythingImage.file.details.image.height,
        width: collectionExploreEverythingImage.file.details.image.width,
        loading: 'eager',
      })

      const allOption = new SubCollection({
        id: ALL_SUBCOLLECTION_FILTER_ID,
        title: ALL_SUBCOLLECTION_TITLE,
        imageDesktop,
        slug: path.split('/').slice(0, -1).join('/') || path, // Parent path or current path if no parent
      })

      return [allOption, ...subCollections]
    }

    return subCollections
  }, [subCollections, isNewCLPFiltersExperimentActive, collectionExploreEverythingImage, path])

  const hasSlider = !isDesktop || allSubCollections.length > 9

  const classes = classnames(
    styles.container,
    className,
    !hasSlider ? styles.container__flat : undefined,
    hasSlider ? styles.container__slider : undefined,
    isNewCLPFiltersExperimentActive ? styles.newSubCollectionFilter : undefined,
  )

  const imageMaxWidth = getMaxWidthInPx({
    maxWidth: styles.subCollectionCardImageWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  // Get header height when component mounts and when scrolling direction changes
  useEffect(() => {
    if (isBrowser()) {
      const getHeaderHeight = () => {
        const headerElement = document.getElementById('site-primary-header')
        const subCategoryListElement = document.getElementById('sub-category-list-header')
        const headerHeight = headerElement?.getBoundingClientRect().height
        const subCategoryListHeight = subCategoryListElement?.getBoundingClientRect().height

        setHeaderHeight((headerHeight ?? 0) + (subCategoryListHeight ?? 0))
      }

      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight)

      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }
  }, [globalIsScrollingDown])

  const handleCardClick = useCallback(
    (subCollection: SubCollection) => {
      trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK, {
        slug: subCollection.slug,
        title: subCollection.title,
      })
      onCardClick(subCollection)
    },
    [onCardClick],
  )

  // Check if any subcollection filter is selected
  const hasAnySubcollectionSelected = useCallback(() => {
    if (!isSubCollectionSelected) return false

    // Skip the first item (All) when checking non-All selections
    return allSubCollections.slice(1).some(sc => isSubCollectionSelected(sc))
  }, [allSubCollections, isSubCollectionSelected])

  // Determine if a subcollection is selected
  const isSubcollectionSelectedWithAll = useCallback(
    (subCollection: SubCollection) => {
      if (!isSubCollectionSelected) return path === subCollection.slug

      // If it's the "All" card (first one)
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        // "All" is selected when no other subcollection is selected
        return !hasAnySubcollectionSelected()
      }
      // For other subcollections, use the original logic
      return isSubCollectionSelected(subCollection)
    },
    [isSubCollectionSelected, path, hasAnySubcollectionSelected],
  )

  // Custom click handler to handle the "All" card differently
  const handleSubcollectionClick = useCallback(
    (subCollection: SubCollection) => {
      // If this is the "All" option
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        // If any other filters are selected, reset them all
        if (hasAnySubcollectionSelected()) {
          // Reset all filters by calling onCardClick with the "All" option
          onCardClick(subCollection)
        }
        // If "All" is already selected, do nothing
        return
      }
      // For normal subcollections, use the original click handler
      handleCardClick(subCollection)
    },
    [handleCardClick, hasAnySubcollectionSelected, onCardClick],
  )

  const renderCard = (subCollection: SubCollection, index: number, isNewFilter: boolean) => {
    const imageProps = ImageDomain.makeImageProps(subCollection.imageDesktop)

    const isSelected = isSubcollectionSelectedWithAll(subCollection)

    const cardClasses = classnames(
      styles.subCollectionCard,
      isNewFilter ? styles.newSubCollectionCardFilter : undefined,
      isNewFilter && index === 0 ? styles.firstCardExperiment : undefined,
      isNewFilter && index === allSubCollections.length - 1 ? styles.lastCardExperiment : undefined,
      !hasSlider ? styles.subCollectionCard__withBorder : undefined,
      isSelected ? styles.subCollectionCard__selected : undefined,
      !hasSlider && isSelected ? styles.subCollectionCard__selected__flat : undefined,
      hasSlider && isSelected && !isNewCLPFiltersExperimentActive
        ? styles.subCollectionCard__selected__slider
        : undefined,
      index === allSubCollections.length - 1 ? styles.subCollectionCard__lastCard : undefined,
      showMobileFilterWithDrawer ? styles.newSubCollectionCardFilter : undefined,
    )

    const imageElement = isDesktop ? (
      <Image
        alt={imageProps.alt}
        {...imageProps}
        className={
          isNewFilter ? styles.subCollectionCard__image__newSubCollectionCardFilter : styles.subCollectionCard__image
        }
        maxWidth={imageMaxWidth}
        loading="eager"
      />
    ) : null

    const titleElement = (
      <h5
        className={classnames(styles.subCollectionCard__text, isNewFilter && styles.subCollectionCard__text__newFilter)}
        data-content-type={SUB_COLLECTION_CARD_TITLE_DATA_TYPE}
        data-slug={subCollection.slug}
        data-active={isSelected}
      >
        {getDisplayKey(subCollection, 'displayTitle', subCollection.title)}
      </h5>
    )

    return (
      <Link
        className={cardClasses}
        key={subCollection.id}
        onClick={() => {
          handleSubcollectionClick(subCollection)
        }}
      >
        {isNewCLPFiltersDesktopActive ? (
          <>
            {imageElement}
            {titleElement}
          </>
        ) : (
          <>
            {titleElement}
            {imageElement}
          </>
        )}
      </Link>
    )
  }

  const renderCards = (isNewFilter = false) => {
    return allSubCollections.map((subCollection, index) => renderCard(subCollection, index, isNewFilter))
  }

  const cardContent = renderCards(isNewCLPFiltersExperimentActive)

  if (showLoader && subCollections.length > 0) {
    return (
      <div className={styles.subCollectionCardLoader}>
        {new Array(8).fill(0).map((_, index) => (
          <div key={index} className={styles.subCollectionCardLoaderItem}>
            <Skeleton className={styles.subCollectionCardLoaderImage} />
            <Skeleton className={styles.subCollectionCardLoaderTitle} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      {hasSlider ? (
        <div
          className={classnames(
            isMobile && isScrolledDown && !globalIsScrollingDown ? styles.fixedSubcollection : '',
            showMobileFilterWithDrawer && isScrolledDown && !globalIsScrollingDown
              ? styles.fixedSubcollectionExperiment
              : '',
            isMobile ? styles.subcollectionSticky : '',
          )}
          style={
            showMobileFilterWithDrawer && isScrolledDown && !globalIsScrollingDown
              ? { top: `${headerHeight}px` }
              : undefined
          }
          id="subcollection-card-list"
        >
          <GridSlider
            navPosition={NAV_POSITION.LEFT_RIGHT}
            hideBorderTop
            hideCenterLine
            className={classes}
            swiperStyles={classnames(styles.swiperStyles, {
              [`${styles.hideBorder}`]: isNewCLPFiltersExperimentActive,
              [`${styles.newCLPFiltersSwiperStyles}`]: isNewCLPFiltersExperimentActive,
            })}
            slideClassName={classnames(styles.slideClassName, {
              [`${styles.slideClassName__newCLPFiltersSlide}`]: isNewCLPFiltersExperimentActive,
            })}
            prevIconClass={classnames(styles.iconSize, styles.prevIcon, {
              [`${styles.iconSize__circle}`]: isNewCLPFiltersExperimentActive,
              [`${styles.iconSize__circle__prevIcon}`]: isNewCLPFiltersExperimentActive,
            })}
            nextIconClass={classnames(styles.iconSize, {
              [`${styles.iconSize__circle}`]: isNewCLPFiltersExperimentActive,
              [`${styles.iconSize__circle__nextIcon}`]: isNewCLPFiltersExperimentActive,
            })}
            speed={isMobile ? 200 : undefined}
            swiperId={`subcollection_card_list-${contentful_id}`}
            isChevronIcon={true}
            isIconWithBG={true}
            id={`subcollection_card_list-${contentful_id}`}
          >
            {cardContent}
          </GridSlider>
        </div>
      ) : (
        <div
          className={classnames(
            classes,
            isMobile && isScrolledDown && !globalIsScrollingDown ? styles.fixedSubcollection : '',
            showMobileFilterWithDrawer && isScrolledDown && !globalIsScrollingDown
              ? styles.fixedSubcollectionExperiment
              : '',
            isMobile ? styles.subcollectionSticky : '',
          )}
          id="subcollection-card-list"
          style={
            showMobileFilterWithDrawer && isScrolledDown && !globalIsScrollingDown
              ? { top: `${headerHeight}px` }
              : undefined
          }
        >
          {cardContent}
        </div>
      )}
      <div ref={intersectRef}></div>
    </>
  )
}

export default SubCollectionCardList
